import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { 
  CreditCard, Check, Loader2, AlertTriangle, Shield, Lock
} from 'lucide-react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';

// Initialize Stripe
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY || 'pk_test_placeholder');

// PayPal icon component
const PayPalIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944.901C5.026.382 5.474 0 5.998 0h7.46c2.57 0 4.578.543 5.69 1.81 1.01 1.15 1.304 2.42 1.012 4.287-.023.143-.047.288-.077.437-.983 5.05-4.349 6.797-8.647 6.797h-2.19c-.524 0-.968.382-1.05.9l-1.12 7.106zm14.146-14.42a3.35 3.35 0 0 0-.607-.541c1.166.12 2.037.36 2.619.723 1.004.625 1.296 1.596 1.084 2.883-.41 2.492-1.71 4.193-3.926 5.203-1.23.56-2.721.874-4.477.874h-2.86c-.397 0-.735.29-.796.68l-.502 3.168-.502 3.169a.641.641 0 0 1-.633.54H6.76a.385.385 0 0 1-.38-.444l.31-1.973.502-3.168c.061-.39.399-.68.796-.68h.925c4.88 0 8.743-1.982 9.861-7.713.153-.784.203-1.508.145-2.17z"/>
  </svg>
);

// Card Element styles
const CARD_ELEMENT_OPTIONS = {
  style: {
    base: {
      color: '#e2e8f0',
      fontFamily: 'system-ui, sans-serif',
      fontSmoothing: 'antialiased',
      fontSize: '16px',
      '::placeholder': {
        color: '#64748b',
      },
    },
    invalid: {
      color: '#ef4444',
      iconColor: '#ef4444',
    },
  },
};

// Stripe Payment Form Component
function StripePaymentForm({ request, onSuccess }) {
  const { api } = useAuth();
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!stripe || !elements) {
      return;
    }

    setProcessing(true);
    setError(null);

    try {
      // Create payment intent
      const { data } = await api.post(`/roadside/requests/${request.id}/payment/stripe`);
      
      // Confirm payment
      const result = await stripe.confirmCardPayment(data.client_secret, {
        payment_method: {
          card: elements.getElement(CardElement),
        },
      });

      if (result.error) {
        setError(result.error.message);
      } else if (result.paymentIntent.status === 'succeeded') {
        // Confirm with backend
        await api.post(`/roadside/requests/${request.id}/payment/confirm`, null, {
          params: { payment_intent_id: result.paymentIntent.id }
        });
        toast.success('Payment successful!');
        onSuccess();
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Payment failed. Please try again.');
    }

    setProcessing(false);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="p-4 rounded-lg border border-slate-700 bg-slate-800/50">
        <CardElement options={CARD_ELEMENT_OPTIONS} />
      </div>
      
      {error && (
        <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-sm">
          <AlertTriangle className="h-4 w-4 inline mr-2" />
          {error}
        </div>
      )}
      
      <Button 
        type="submit" 
        className="w-full bg-blue-600 hover:bg-blue-500 h-12"
        disabled={!stripe || processing}
        data-testid="stripe-pay-btn"
      >
        {processing ? (
          <>
            <Loader2 className="h-5 w-5 animate-spin mr-2" />
            Processing...
          </>
        ) : (
          <>
            <CreditCard className="h-5 w-5 mr-2" />
            Pay ${request.platform_fee?.toFixed(2)}
          </>
        )}
      </Button>
    </form>
  );
}

export default function RoadsidePaymentPage() {
  const { requestId } = useParams();
  const [searchParams] = useSearchParams();
  const { api } = useAuth();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [request, setRequest] = useState(null);
  const [paymentComplete, setPaymentComplete] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('card'); // 'card' or 'paypal'
  const [processingPayPal, setProcessingPayPal] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchRequest();
    // Handle PayPal return
    handlePayPalReturn();
  }, [requestId]);

  const fetchRequest = async () => {
    try {
      const res = await api.get(`/roadside/requests/${requestId}`);
      setRequest(res.data);
      if (res.data.customer_paid) {
        setPaymentComplete(true);
      }
    } catch (error) {
      toast.error('Request not found');
      navigate('/roadside');
    }
    setLoading(false);
  };

  const handlePayPalReturn = async () => {
    const paypalStatus = searchParams.get('paypal');
    const payerId = searchParams.get('PayerID');
    const paymentId = searchParams.get('paymentId');
    
    if (paypalStatus === 'success' && payerId && paymentId) {
      setProcessingPayPal(true);
      try {
        await api.post(`/roadside/requests/${requestId}/payment/paypal/execute`, {
          payer_id: payerId,
          payment_id: paymentId
        });
        toast.success('PayPal payment successful!');
        setPaymentComplete(true);
      } catch (err) {
        setError(err.response?.data?.detail || 'PayPal payment failed');
        toast.error('PayPal payment failed');
      }
      setProcessingPayPal(false);
    } else if (paypalStatus === 'cancel') {
      toast.info('Payment cancelled');
    }
  };

  const handlePaymentSuccess = () => {
    setPaymentComplete(true);
  };

  const handlePayPalPayment = async () => {
    setProcessingPayPal(true);
    setError(null);
    
    try {
      const { data } = await api.post(`/roadside/requests/${requestId}/payment/paypal`);
      
      if (data.approval_url) {
        // Redirect to PayPal
        window.location.href = data.approval_url;
      } else {
        throw new Error('No approval URL received');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to initiate PayPal payment');
      setProcessingPayPal(false);
    }
  };

  if (loading || processingPayPal) {
    return (
      <div className="min-h-screen bg-slate-950 flex flex-col items-center justify-center gap-4">
        <Loader2 className="h-8 w-8 animate-spin text-blue-400" />
        {processingPayPal && <p className="text-slate-400">Processing PayPal payment...</p>}
      </div>
    );
  }

  if (paymentComplete) {
    return (
      <div className="min-h-screen bg-slate-950 p-4 md:p-6 flex items-center justify-center">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 rounded-full bg-green-500/10 border border-green-500/30 flex items-center justify-center mx-auto mb-4">
              <Check className="h-8 w-8 text-green-400" />
            </div>
            <h2 className="text-2xl font-bold mb-2">Request Submitted!</h2>
            <p className="text-slate-400 mb-6">
              Nearby providers are being notified. You'll receive updates when someone accepts your request.
            </p>
            <div className="space-y-3">
              <Button className="w-full" onClick={() => navigate(`/roadside/track/${requestId}`)} data-testid="track-request-btn">
                Track Your Request
              </Button>
              <Button variant="outline" className="w-full" onClick={() => navigate('/roadside')}>
                Back to Home
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 p-4 md:p-6" data-testid="roadside-payment-page">
      <div className="max-w-md mx-auto">
        <Card>
          <CardHeader className="text-center">
            <CardTitle>Complete Payment</CardTitle>
            <CardDescription>
              Pay the platform fee to submit your request
            </CardDescription>
          </CardHeader>
          
          <CardContent className="space-y-6">
            {/* Request Summary */}
            <div className="p-4 rounded-lg bg-slate-800/50 border border-slate-700">
              <h3 className="font-medium mb-3">Request Summary</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-slate-400">Service</span>
                  <span>{request?.service_name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400">Location</span>
                  <span>{request?.location?.city}, {request?.location?.state}</span>
                </div>
                {request?.vehicle && (
                  <div className="flex justify-between">
                    <span className="text-slate-400">Vehicle</span>
                    <span>{request.vehicle.year} {request.vehicle.make}</span>
                  </div>
                )}
              </div>
            </div>
            
            {/* Fee Breakdown */}
            <div className="p-4 rounded-lg border border-blue-500/30 bg-blue-500/5">
              <div className="flex justify-between items-center">
                <div>
                  <p className="font-medium">Platform Fee</p>
                  <p className="text-xs text-slate-400">One-time connection fee</p>
                </div>
                <p className="text-2xl font-bold text-blue-400">
                  ${request?.platform_fee?.toFixed(2)}
                </p>
              </div>
              
              {request?.promo_applied && (
                <div className="mt-2 pt-2 border-t border-blue-500/20 flex justify-between text-sm">
                  <span className="text-green-400">Promo: {request.promo_applied.code}</span>
                  <span className="text-green-400">-${request.promo_applied.discount.toFixed(2)}</span>
                </div>
              )}
              
              {request?.is_surge && (
                <Badge className="mt-2 bg-amber-500/20 text-amber-400 border-amber-500/30">
                  Surge pricing in effect
                </Badge>
              )}
            </div>
            
            {/* Payment Method Selection */}
            <div className="space-y-3">
              <label className="text-sm font-medium text-slate-300">Payment Method</label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => setPaymentMethod('card')}
                  className={`p-4 rounded-lg border-2 transition-all flex flex-col items-center gap-2 ${
                    paymentMethod === 'card'
                      ? 'border-blue-500 bg-blue-500/10'
                      : 'border-slate-700 hover:border-slate-600'
                  }`}
                  data-testid="select-card-btn"
                >
                  <CreditCard className={`h-6 w-6 ${paymentMethod === 'card' ? 'text-blue-400' : 'text-slate-400'}`} />
                  <span className={`text-sm font-medium ${paymentMethod === 'card' ? 'text-blue-400' : 'text-slate-300'}`}>
                    Credit Card
                  </span>
                </button>
                
                <button
                  type="button"
                  onClick={() => setPaymentMethod('paypal')}
                  className={`p-4 rounded-lg border-2 transition-all flex flex-col items-center gap-2 ${
                    paymentMethod === 'paypal'
                      ? 'border-[#0070ba] bg-[#0070ba]/10'
                      : 'border-slate-700 hover:border-slate-600'
                  }`}
                  data-testid="select-paypal-btn"
                >
                  <PayPalIcon className={`h-6 w-6 ${paymentMethod === 'paypal' ? 'text-[#0070ba]' : 'text-slate-400'}`} />
                  <span className={`text-sm font-medium ${paymentMethod === 'paypal' ? 'text-[#0070ba]' : 'text-slate-300'}`}>
                    PayPal
                  </span>
                </button>
              </div>
            </div>
            
            {/* Error Display */}
            {error && (
              <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-sm">
                <AlertTriangle className="h-4 w-4 inline mr-2" />
                {error}
              </div>
            )}
            
            {/* Payment Form - Stripe */}
            {paymentMethod === 'card' && (
              <Elements stripe={stripePromise}>
                <StripePaymentForm request={request} onSuccess={handlePaymentSuccess} />
              </Elements>
            )}
            
            {/* Payment Button - PayPal */}
            {paymentMethod === 'paypal' && (
              <div className="space-y-4">
                <Button 
                  type="button"
                  onClick={handlePayPalPayment}
                  className="w-full bg-[#0070ba] hover:bg-[#005ea6] h-12"
                  disabled={processingPayPal}
                  data-testid="paypal-pay-btn"
                >
                  {processingPayPal ? (
                    <>
                      <Loader2 className="h-5 w-5 animate-spin mr-2" />
                      Redirecting to PayPal...
                    </>
                  ) : (
                    <>
                      <PayPalIcon className="h-5 w-5 mr-2" />
                      Pay with PayPal
                    </>
                  )}
                </Button>
                <p className="text-xs text-slate-500 text-center">
                  You will be redirected to PayPal to complete your payment
                </p>
              </div>
            )}
            
            {/* Trust Badges */}
            <div className="flex items-center justify-center gap-6 pt-4 border-t border-slate-800">
              <div className="flex items-center gap-1 text-xs text-slate-400">
                <Shield className="h-4 w-4" />
                <span>Secure</span>
              </div>
              <div className="flex items-center gap-1 text-xs text-slate-400">
                <Lock className="h-4 w-4" />
                <span>Encrypted</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
